/**
 * SnapshotSelectSwings Page
 * Grid of swing thumbnails with checkboxes
 * Real-time counter showing "X of 5 selected"
 * Empty state if < 5 swings available
 * Sticky footer with disabled CTA until exactly 5 selected
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { SnapshotProgressHeader } from '@/components/snapshot/SnapshotProgressHeader';
import { CheckboxSwingSelector } from '@/components/snapshot/CheckboxSwingSelector';
import { StickyFooterButton } from '@/components/snapshot/StickyFooterButton';
import { SnapshotProcessing } from '@/components/snapshot/SnapshotProcessing';
import { getAvailableSwings, createSnapshotV2 } from '@/lib/api/snapshot';
import { GolfCard, GolfCardContent } from '@/components/golf/golf-card';
import { Loader2 } from 'lucide-react';
import { BottomTabs } from '@/mvp/navigation/BottomTabs';

export default function SnapshotSelectSwings() {
  const navigate = useNavigate();
  const [swings, setSwings] = useState<Array<{
    id: string;
    created_at: string;
    thumbnail_url: string | null;
    filename: string | null;
    has_analysis: boolean;
  }>>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadSwings();
  }, []);

  const loadSwings = async () => {
    try {
      setLoading(true);
      const available = await getAvailableSwings();
      setSwings(available);
    } catch (error) {
      console.error('Error loading swings:', error);
      toast.error('Failed to load swings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size < 5) {
          next.add(id);
        } else {
          toast.error('You can only select 5 swings');
        }
      }
      return next;
    });
  };

  const handleCreateSnapshot = async () => {
    if (selectedIds.size !== 5) {
      toast.error('Please select exactly 5 swings');
      return;
    }

    try {
      setProcessing(true);
      const swingIdsArray = Array.from(selectedIds);
      const result = await createSnapshotV2(swingIdsArray);
      
      // Navigate to results page using snapshot_v2_run_id
      navigate(`/snapshot/results/${result.snapshot_v2_run_id}`);
    } catch (error: any) {
      console.error('Error creating snapshot:', error);
      toast.error(error.message || 'Failed to create snapshot');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty state if < 5 swings available
  if (swings.length < 5) {
    return (
      <div className="min-h-screen bg-background p-6">
        <SnapshotProgressHeader currentStep={1} totalSteps={3} />
        <GolfCard className="mt-8">
          <GolfCardContent className="text-center py-12">
            <p className="text-lg font-semibold text-foreground mb-2">
              Not enough swings available
            </p>
            <p className="text-muted-foreground mb-4">
              You need at least 5 analyzed swings to create a snapshot.
            </p>
            <p className="text-sm text-muted-foreground">
              You currently have {swings.length} analyzed swing{swings.length !== 1 ? 's' : ''}.
            </p>
          </GolfCardContent>
        </GolfCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {processing && <SnapshotProcessing />}
      
      <div className="p-6 space-y-6">
        <SnapshotProgressHeader currentStep={1} totalSteps={3} />

        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Select 5 Swings
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Choose 5 swings that have been analyzed to create your snapshot.
          </p>

          {/* Real-time counter */}
          <div className="mb-4 p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium text-foreground">
              {selectedIds.size} of 5 selected
            </p>
          </div>

          {/* Swing Grid */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {swings.map(swing => (
              <CheckboxSwingSelector
                key={swing.id}
                id={swing.id}
                thumbnailUrl={swing.thumbnail_url}
                filename={swing.filename}
                hasAnalysis={swing.has_analysis}
                selected={selectedIds.has(swing.id)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Sticky Footer CTA */}
      <StickyFooterButton
        onClick={handleCreateSnapshot}
        disabled={selectedIds.size !== 5 || processing}
      >
        {selectedIds.size === 5 ? 'Create Snapshot' : `Select ${5 - selectedIds.size} more swing${5 - selectedIds.size !== 1 ? 's' : ''}`}
      </StickyFooterButton>
      
      {/* Bottom Navigation */}
      <BottomTabs />
    </div>
  );
}


